import { FormEvent, useState } from 'react';
import { sendEstimateRequest, validateEstimate } from '../utils/estimate';
import type { EstimateFormErrors, EstimateFormValues, SubmitStatus } from '../types/estimate';

const initialValues: EstimateFormValues = {
  name: '',
  phone: '',
  email: '',
  vehicle: '',
  service: '',
  claimType: 'insurance',
  description: '',
};

const MAX_PHOTOS = 10;

export function useEstimateForm() {
  const [values, setValues] = useState<EstimateFormValues>(initialValues);
  const [photos, setPhotos] = useState<File[]>([]);
  const [errors, setErrors] = useState<EstimateFormErrors>({});
  const [status, setStatus] = useState<SubmitStatus>('idle');

  function updateField<K extends keyof EstimateFormValues>(field: K, value: EstimateFormValues[K]) {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (status === 'error') setStatus('idle');
  }

  function updatePhotos(fileList: FileList | null) {
    setPhotos(fileList ? Array.from(fileList).slice(0, MAX_PHOTOS) : []);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validateEstimate(values);
    setErrors(nextErrors);

    const firstError = Object.keys(nextErrors)[0];
    if (firstError) {
      document.getElementById(`estimate-${firstError}`)?.focus();
      return;
    }

    setStatus('submitting');
    try {
      await sendEstimateRequest(values, photos);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  function reset() {
    setValues(initialValues);
    setPhotos([]);
    setErrors({});
    setStatus('idle');
  }

  return { values, photos, errors, status, updateField, updatePhotos, submit, reset, maxPhotos: MAX_PHOTOS };
}
